<script setup lang="ts">
import { T } from '@tolgee/vue'
import BaseLinkButton from '@/components/base/BaseLinkButton.vue'
import LuckyRushTicketsModal from '@/components/modals/LuckyRushTicketsModal.vue'
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { formatNumberFullTruncate, formatUnshortenedNumber } from '@/helpers/utils'
import { useGiveaway } from '@/composables/game/giveaway'
import BaseButton from '@/components/base/BaseButton.vue'
import { formatDuration } from '@/helpers/date'
import { type GiveawayPrize, LuckyRushTab } from '@/types/giveaway'
import { CHALLENGE_PRIZE_TYPE } from '@/types/shared/player-config'
import ScrollIndicator from '@/components/ScrollIndicator.vue'
import GiveawayPrizeBadge from '@/components/GiveawayPrizeBadge.vue'
import CountdownDisplay from '@/components/CountdownDisplay.vue'
import { DateTime } from 'luxon'
import ArrowRightIcon from '@/components/icons/ArrowRightIcon.vue'
import { usePlayer } from '@/composables/game/player'
import { useTonConnectModal } from '@townsquarelabs/ui-vue'
import PlayerAvatar from '@/components/PlayerAvatar.vue'
import { Vue3Lottie } from 'vue3-lottie'
import ViewGiftModal from '@/components/modals/ViewGiftModal.vue'
import type { Nullable } from '@/types/shared/common'
import { useFullScreenDimensions } from '@/composables/helpers/fullscreen'
import { useGiftModal } from '@/composables/game/gift-modal'
import USDTimage from '@/assets/images/usdt.webp'
import TONimage from '@/assets/images/ton-symbol.png'
import DROPEEimage from '@/assets/images/dropee-token-new.webp'

const props = defineProps<{
	activeTab: LuckyRushTab
}>()

const {
  countdown,
  playerTickets,
  totalTickets,
  hasWinners,
  isWinner,
  drawCountdown,
  winners,
  userPosition,
  previousPlayerTickets,
  previousTotalTickets,
  giveawayPreviousEndTimestamp,
  giveaway,
  prizeList,
  prizeListForWinners,
  previousGiveaway,
  previousPrizeList,
  previousPrizeListForWinners,
  totalPrize,
  previousTotalPrize,
  prizeTypeImage,
  previousPrizeTypeImage,
} = useGiveaway()
const { player } = usePlayer()
const { open } = useTonConnectModal()
const { bottomNumber } = useFullScreenDimensions()
const { setSelectedGift } = useGiftModal()

const showGiftModal = ref(false)
const selectedGift = ref<Nullable<GiveawayPrize>>(null)

const isLiveTab = computed(() => props.activeTab === LuckyRushTab.LIVE)

const activePrizeType = computed(() =>
  props.activeTab === LuckyRushTab.PREVIOUS
    ? previousGiveaway.value?.prizeType
    : giveaway.value?.prizeType,
)

const activePrizeTypeImage = computed(() =>
  props.activeTab === LuckyRushTab.PREVIOUS
    ? previousPrizeTypeImage.value
    : prizeTypeImage.value,
)

const activeTotalPrize = computed(() =>
  props.activeTab === LuckyRushTab.PREVIOUS
    ? previousTotalPrize.value
    : totalPrize.value,
)

const bottomStyles = computed(() => {
  return { bottom: `${bottomNumber.value}px` }
})

const playerWalletAddress = computed(() => player.value.settings.tonWalletAddress)

const showLuckyRushTicketsModal = ref<boolean>(false)
const pulseTickets = ref(false)

const scrollContainer = ref<HTMLElement | null>(null)
const listLimit = ref(100)
const loadMoreList = ref(true)

const playerTicketsToShow = computed(() => props.activeTab === LuckyRushTab.LIVE ? playerTickets.value : previousPlayerTickets.value)
const totalTicketsToShow = computed(() => props.activeTab === LuckyRushTab.LIVE ? totalTickets.value : previousTotalTickets.value)

const showWinners = computed(() => hasWinners.value && props.activeTab === LuckyRushTab.PREVIOUS)
const listToShow = ref(previousPrizeListForWinners.value.slice(0, listLimit.value))
const inScrollView = ref(true)

const container = ref<HTMLElement | null>(null)

const previousEndDate = computed(() => DateTime.fromSeconds(giveawayPreviousEndTimestamp.value).toFormat('DD'))

const listToIterate = computed<GiveawayPrize[]>(() => {
  if (props.activeTab === LuckyRushTab.PREVIOUS) {
    return showWinners.value
      ? previousPrizeListForWinners.value.slice(0, listLimit.value)
      : previousPrizeList.value
  }
  return prizeList.value
})

function onLoad() {
  console.log('on load')
  const sourceList = previousPrizeListForWinners.value
  const newLimit = Math.min(listLimit.value + 100, sourceList.length)
  listToShow.value.push(...sourceList.slice(listLimit.value, newLimit))
  listLimit.value = newLimit
}

const imageMapping = {
  'DROPEE': DROPEEimage,
  'USDT': USDTimage,
  'TON': TONimage,
} as const

function calculateScrollPosition() {
  if (!scrollContainer.value || !showWinners.value) return
  if (container.value && userPosition.value) {
    const cardElement = document.getElementsByClassName('card')[userPosition.value - 1] as HTMLElement
    if (cardElement) {
      const cardRect = cardElement.getBoundingClientRect()
      inScrollView.value = cardRect.bottom <= window.innerHeight - 40
    }
  }
  const epsilon = 20
  if (scrollContainer.value.scrollTop + scrollContainer.value.clientHeight + epsilon >= Math.ceil(scrollContainer.value.scrollHeight) && listToShow.value.length < previousPrizeListForWinners.value.length) {
    loadMoreList.value = false
    onLoad()
    setTimeout(() => {
      loadMoreList.value = true
    }, 200)
  }
}

watch(() => playerTickets.value, (value, prevValue) => {
  if (prevValue !== undefined && value > prevValue) {
    pulseTickets.value = true
    setTimeout(() => {
      pulseTickets.value = false
    }, 3000)
  }
}, { immediate: true })

watch([container, userPosition, scrollContainer, showWinners], () => {
  nextTick(() => {
    calculateScrollPosition()
  })
}, { immediate: true })

onMounted(() => {
  setTimeout(() => {
    scrollContainer.value = document.querySelector('.scroll-container')
    if (!scrollContainer.value) return
    scrollContainer.value.addEventListener('scroll', calculateScrollPosition)
  }, 500)
})

onBeforeUnmount(() => {
  if (!scrollContainer.value) return
  scrollContainer.value.removeEventListener('scroll', calculateScrollPosition)
})

const prizesText = computed(() => {
  if (giveaway.value?.prizeType === CHALLENGE_PRIZE_TYPE.USDT) {
    return `${formatUnshortenedNumber(totalPrize.value)} USDT PRIZE POOL`
  } else if (giveaway.value?.prizeType === CHALLENGE_PRIZE_TYPE.TON) {
    return `${formatUnshortenedNumber(totalPrize.value)} TON PRIZE POOL`
  } else if (giveaway.value?.prizeType === CHALLENGE_PRIZE_TYPE.DROPEE) {
    return `${formatUnshortenedNumber(totalPrize.value)} DROPEE PRIZE POOL`
  } else {
    return 'WIN TELEGRAM GIFTS!'
  }
})

</script>
<template>
  <div
    ref="container"
    class="w-full flex flex-col relative"
    :class="countdown ? 'pb-4' : 'pb-10'"
  >
    <div
      v-if="isLiveTab"
      class="text-teal-primary text-center text-base font-semibold"
    >
      {{ prizesText }}
    </div>
    <p
      class="text-center text-white/70 text-base tracking-tight font-semibold mb-3 whitespace-pre-line"
    >
      <template v-if="isLiveTab">
        {{ `The more you collect tickets, the better your chances to win big! 🚀\n` }}
        <span
          @click="open"
          class="underline"
        >Connect</span> your wallet to enter the giveaway.
      </template>
      <template v-else>
        Prizes will be sent shortly after the end of the giveaway. Make sure to <span
          class="text-teal-primary underline"
          @click="open"
        >connect</span> your wallet to receive your prize!
      </template>
    </p>
    <BaseButton
      v-if="!playerWalletAddress"
      class="mb-3"
      @click="open"
    >
      <T
        key-name="lucky_rush.connect_wallet"
        default-value="CONNECT WALLET"
      />
      <template #icon>
        <ArrowRightIcon />
      </template>
    </BaseButton>
    <BaseLinkButton
      v-if="isLiveTab"
      class="mb-3"
      @click="showLuckyRushTicketsModal = true"
    >
      <T
        key-name="rewards.extra_tab.how_to_get"
        default-value="How to get tickets and enter"
      />
    </BaseLinkButton>
    <div class="mt-3 mb-4 grid grid-cols-2 items-center w-full gap-x-4 px-4">
      <div class=" gray-background rounded-xl w-full">
        <div
          class="green-linear-background  rounded-xl p-0.5"
          :class="countdown ? pulseTickets ? 'highlighted-tickets' : 'pulse-shadow-orange' : ''"
        >
          <div class="flex flex-col items-center px-4 py-3 text-white bg-black rounded-xl">
            <span class="text-base font-semibold">
              <T
                key-name="rewards.extra_tab.your_entries"
                default-value="Your entries"
              />
            </span>
            <span class="text-2xl font-semibold">{{
              playerTicketsToShow >= 100_000 ? formatNumberFullTruncate(playerTicketsToShow) : formatUnshortenedNumber(playerTicketsToShow)
            }}</span>
          </div>
        </div>
      </div>

      <div
        class="flex flex-col items-center px-4 py-3 w-full border-2 gray-border rounded-xl text-white/[87%]"
      >
        <span class="text-base font-semibold">
          <T
            key-name="rewards.extra_tab.your_entries"
            default-value="Total entries"
          />
        </span>
        <span class="text-2xl font-semibold">{{ formatNumberFullTruncate(totalTicketsToShow) }}</span>
      </div>
    </div>
    <div
      class="bg-dark-most-bottom-layer p-4 rounded-3xl flex flex-col w-full gap-y-4"
      :class="!isLiveTab && !isWinner ? 'mb-[60px]' : 'mb-4'"
    >
      <CountdownDisplay
        v-if="isLiveTab"
        :countdown="countdown"
      />
      <div
        v-else
        class="border-2 gray-border bg-dark-bottom-layer py-3 flex flex-col items-center rounded-xl"
      >
        <span class="text-sm font-semibold text-white/60 tracking-tight">
          <T
            key-name="lucky_rush.ended"
            default-value="Ended"
          />
        </span>
        <span class="font-medium text-[32px] leading-8">{{ previousEndDate }}</span>
      </div>
      <div
        v-for="(item, index) in listToIterate"
        :key="index"
        class="card flex items-center justify-between gap-x-2 w-full p-2 rounded-xl bg-dark-bottom-layer"
        :class="index+1 === userPosition && showWinners && 'border-2 border-teal-primary'"
      >
        <div class="flex items-center gap-x-3 overflow-hidden w-full">
          <GiveawayPrizeBadge
            :position="index+1"
            :show-winners="showWinners"
          />
          <div class="flex items-center gap-x-2 overflow-hidden">
            <PlayerAvatar
              v-if="showWinners && winners?.[index]"
              class="size-6 min-w-[24px]"
              :image-src="winners[index].photoUrl ?? undefined"
              :first-name="winners[index].firstName"
              :outer-border-style="false"
              custom-class="p-0"
            />
            <span
              class="text-base font-semibold text-white truncate block w-full"
            >{{ showWinners && winners?.[index] ? winners[index].firstName : item.label }}</span>
          </div>
        </div>
        <div
          v-if="activePrizeType === CHALLENGE_PRIZE_TYPE.TON || activePrizeType === CHALLENGE_PRIZE_TYPE.USDT || activePrizeType === CHALLENGE_PRIZE_TYPE.DROPEE"
          class="flex items-center gap-x-2.5"
        >
          <span
            class="text-sm font-medium -tracking-[0.28px] text-nowrap"
          >{{ `${+parseFloat(item.amount!.toString()).toFixed(2)}` }}</span>
          <img
            :src="imageMapping[activePrizeType]"
            class="size-6 min-w-6"
          >
        </div>
        <div
          v-else
          class="size-10"
          @click="setSelectedGift({ name: item.nftMetadata?.name, lottie: item.nftMetadata?.lottie ?? '' })"
        >
          <Vue3Lottie
            class="rounded-lg"
            :animation-link="item.nftMetadata?.lottie"
            :auto-play="true"
            :loop="true"
          />
        </div>
      </div>
    </div>
    <div
      v-if="isLiveTab && !countdown"
      class="fixed w-full max-w-[440px] mx-auto z-[10] flex items-center justify-center px-4 left-1/2 -translate-x-1/2 overflow-visible"
      :style="bottomStyles"
    >
      <BaseButton
        style-type="gold"
        class="w-full pointer-events-none"
        button-class="!shadow-none"
        text-class="opacity-[60%]"
      >
        <T
          key-name="rewards.extra_tab.winners_announced_in"
          default-value="Winners announced in"
        />
        {{ formatDuration(drawCountdown) }}
      </BaseButton>
    </div>
    <div
      v-if="!isLiveTab"
      class="fixed w-full max-w-[440px] mx-auto z-[10] flex items-center justify-center px-4 left-1/2 -translate-x-1/2 overflow-visible"
      :style="bottomStyles"
    >
      <div
        v-if="!isWinner"
        class="py-2 px-3 border-2 border-red-error rounded-xl w-full flex flex-col items-center bg-dark-bottom-layer"
      >
        <div class="flex items-center gap-x-2">
          <img
            src="@/assets/images/emojis/emoji-cry.png"
            class="size-6 min-w-6"
          >
          <span class="font-semibold text-[28px] leading-7">
            <T
              key-name="lucky_rush.loser.title"
              default-value="You didn’t win"
            />
          </span>
        </div>
        <p class="text-base font-semibold text-white/70 text-center">
          <T
            key-name="lucky_rush.loser.description"
            default-value="You didn’t win this round, but try your luck in the next Lucky Rush!"
          />
        </p>
      </div>
      <div
        v-else-if="showWinners && userPosition && winners?.[userPosition - 1] && !inScrollView"
        class="flex items-center justify-between gap-x-2 w-full py-2 px-3 rounded-xl bg-dark-bottom-layer border-2 border-teal-primary"
      >
        <div class="flex items-center gap-x-3 overflow-hidden w-full">
          <GiveawayPrizeBadge
            :position="userPosition"
            show-winners
          />
          <div class="flex items-center gap-x-2 overflow-hidden">
            <PlayerAvatar
              class="size-6 min-w-[24px]"
              :image-src="winners[userPosition -1].photoUrl ?? undefined"
              :first-name="winners[userPosition - 1].firstName"
              :outer-border-style="false"
              custom-class="p-0"
            />
            <span
              class="text-base font-semibold text-white truncate block w-full"
            >{{ winners[userPosition - 1].firstName
            }}</span>
          </div>
        </div>
        <div
          v-if="activePrizeType === CHALLENGE_PRIZE_TYPE.TON || activePrizeType === CHALLENGE_PRIZE_TYPE.USDT || activePrizeType === CHALLENGE_PRIZE_TYPE.DROPEE"
          class="flex items-center gap-x-2.5"
        >
          <span
            class="text-sm font-medium -tracking-[0.28px] text-nowrap"
          >{{ `${+parseFloat(listToIterate[userPosition].amount!.toString()).toFixed(2)}` }}</span>
          <img
            :src="imageMapping[activePrizeType]"
            class="size-6 min-w-6"
          >
        </div>
        <div
          v-else
          class="size-10"
          @click="setSelectedGift({ name: listToIterate[userPosition].nftMetadata?.name, lottie: listToIterate[userPosition].nftMetadata?.lottie ?? '' })"
        >
          <Vue3Lottie
            class="rounded-lg"
            :animation-link="listToIterate[userPosition].nftMetadata?.lottie"
            :auto-play="true"
            :loop="true"
          />
        </div>
      </div>
    </div>
    <LuckyRushTicketsModal
      v-if="showLuckyRushTicketsModal"
      :show="showLuckyRushTicketsModal"
      @hide="showLuckyRushTicketsModal = false"
    />
    <ScrollIndicator
      v-if="activeTab === LuckyRushTab.PREVIOUS"
      :show-down="false"
      :trigger-position="300"
    />
    <ViewGiftModal
      v-if="selectedGift?.nftMetadata"
      :show="showGiftModal"
      :gift="selectedGift.nftMetadata"
      @hide="showGiftModal = false"
    />
  </div>
</template>
<style scoped lang="scss">
.green-linear-background {
	background: linear-gradient(-45deg, rgba(237, 255, 202, 0.22) 13%, #48650C 24%, #14F1FF 42%, #14F1FF 63%, rgba(238, 255, 202, 0.77) 79%, #E2FFA7 82%);
}

.gray-background {
	background: rgba(237, 255, 202, 0.22);
}

.gray-border {
	border-color: rgba(255, 255, 255, 0.38);
}

@keyframes pulse-shadow {
	0% {
		filter: drop-shadow(0px 0px 0px rgba(230, 185, 12, 0)); /* No shadow */
	}
	50% {
		filter: drop-shadow(0px 0px 20px rgba(230, 185, 12, 0.66));
	}
	100% {
		filter: drop-shadow(0px 0px 6px rgba(230, 185, 12, 0));
	}
}

.pulse-shadow-orange {
	animation: pulse-shadow 1.8s linear;
	animation-iteration-count: infinite;
	will-change: filter;
}

.highlighted-tickets {
	animation: scale-up 0.5s ease alternate;
	animation-iteration-count: infinite;
}

@keyframes scale-up {
	from {
		transform: scale(1);
		filter: drop-shadow(0px 0px 0px rgba(230, 185, 12, 0)); /* No shadow */
	}
	to {
		transform: scale(1.03);
		filter: drop-shadow(0px 0px 20px rgba(230, 185, 12, 0.66));
	}
}
</style>
